import { Caregiver } from "@/model/Caregiver";
import { DateTimeFormatter, Instant, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import { ComponentPropsWithoutRef, ReactNode } from "react";
import IconButton from "../../../components/IconButton";
import MaterialIcon from "../../../components/MaterialIcon";
import SkeletonTextLine from "../../../components/SkeletonTextLine";
import BloodGlucoseThresholds from "../../../model/BloodGlucoseThresholds";
import InfoBox from "../InfoBox";

type BloodGlucoseThresholdsBoxProps = {
  thresholds?: BloodGlucoseThresholds;
  protocolName: string;
  lastUpdatedAt?: Instant;
  lastUpdatedBy?: Caregiver;
  isLoading: boolean;
  onEditSelected: () => void;
} & ComponentPropsWithoutRef<"div">;

const DiabetesProtocolBox = ({
  thresholds,
  protocolName,
  lastUpdatedAt,
  lastUpdatedBy,
  isLoading,
  onEditSelected,
  className,
  ...otherProps
}: BloodGlucoseThresholdsBoxProps) => (
  <InfoBox className={className} {...otherProps}>
    <div className="mb-2 flex items-center justify-between gap-2">
      <InfoBox.Header>Protocol - {protocolName}</InfoBox.Header>
      <IconButton variant="neutral" onClick={onEditSelected} disabled={isLoading}>
        <MaterialIcon iconName="edit" style={{ fontSize: "20px" }} />
      </IconButton>
    </div>
    <div className="grid grid-cols-[auto_auto] gap-x-4">
      <div className="flex flex-col">
        <span className="mb-1 underline">Fasting</span>
        <InfoBox.Lines className="gap-x-2">
          <InfoBox.Line
            label="Emergency low:"
            value={<Value value={`≤ ${thresholds?.emergencyLow ?? 0}`} isLoading={isLoading} />}
          />
          <InfoBox.Line
            label="Critical low:"
            value={<Value isLoading={isLoading} value={`≤ ${thresholds?.criticalLow ?? 0}`} />}
          />
          <InfoBox.Line label="-" value={null} />
          <InfoBox.Line
            label="Elevated:"
            value={<Value isLoading={isLoading} value={`≥ ${thresholds?.fastingElevated ?? 0}`} />}
          />
          <InfoBox.Line
            label="Critical high:"
            value={<Value isLoading={isLoading} value={`≥ ${thresholds?.criticalHigh ?? 0}`} />}
          />
        </InfoBox.Lines>
      </div>
      <div className="flex flex-col">
        <span className="mb-1 underline">Postprandial</span>
        <InfoBox.Lines className="gap-x-2">
          <InfoBox.Line
            label="Emergency low:"
            value={<Value value={`≤ ${thresholds?.emergencyLow}`} isLoading={isLoading} />}
          />
          <InfoBox.Line
            label="Critical low:"
            value={<Value isLoading={isLoading} value={`≤ ${thresholds?.criticalLow ?? 0}`} />}
          />
          <InfoBox.Line
            label="Low:"
            value={<Value isLoading={isLoading} value={`≤ ${thresholds?.postprandialLow ?? 0}`} />}
          />
          <InfoBox.Line
            label="Elevated:"
            value={
              <Value isLoading={isLoading} value={`≥ ${thresholds?.postprandialElevated ?? 0}`} />
            }
          />
          <InfoBox.Line
            label="Critical high:"
            value={<Value isLoading={isLoading} value={`≥ ${thresholds?.criticalHigh ?? 0}`} />}
          />
        </InfoBox.Lines>
      </div>
    </div>
    {isLoading ? (
      <SkeletonTextLine className="mb-[3px] mt-[19px] h-[14px] w-48" />
    ) : (
      lastUpdatedAt && (
        <p className="mt-4 max-w-[452px] text-sm italic">
          {lastUpdatedAt
            ? `Modified on ${dateFormatter.format(lastUpdatedAt.atZone(ZoneId.SYSTEM))}${lastUpdatedBy ? ` by ${lastUpdatedBy.displayName}` : ""}`
            : `${protocolName} protocol`}
        </p>
      )
    )}
  </InfoBox>
);

const dateFormatter = DateTimeFormatter.ofPattern("M/d/yyyy").withLocale(Locale.US);

type ValueProps = {
  value?: ReactNode;
  isLoading: boolean;
};

const Value = ({ value, isLoading }: ValueProps) =>
  isLoading ? (
    <SkeletonTextLine className="mt-1 h-4 min-w-[5ch]" />
  ) : (
    <p className="overflow-visible text-nowrap">{value ?? "N/A"}</p>
  );

export default DiabetesProtocolBox;
